/*************************************************
 * ByzFunder
 * @exports
 * AuthApi.ts
 * Created by vinoth kumar on  15/05/2024
 * Copyright © 2024 ByzFunder. All rights reserved.
 *************************************************/

import { createApi } from "@reduxjs/toolkit/query/react";
import CustomFetchBase from "./CustomFetchBase";
import { setUser, setImpersonateUser, setOnlineCheckout} from "../slice/UserSlice";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: CustomFetchBase,
  endpoints: (builder) => ({
    loginUser: builder.mutation({
      query: (body) => ({
        url: `login`,
        method: "POST",
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.data?.users?.is_verified) {
            dispatch(setUser(data.data?.users));
          }
        } catch (error) { }
      },
    }),
    googleAuth: builder.mutation({
      query: (body) => ({
        url: `google-auth`,
        method: "POST",
        body,
      }),
    }),
    logoutUser: builder.mutation({
      query: (body) => ({
        url: `logout`,
        method: "POST",
        body,
      }),
    }),
    forgotPassword: builder.mutation({
      query: (body) => ({
        url: `send-forgot-password-otp`,
        method: "POST",
        body,
      }),
    }),
    changePassword: builder.mutation({
      query: (body) => ({
        url: `rest-password`,
        method: "POST",
        body,
      }),
    }),
    registerUser: builder.mutation({
      query: (body) => ({
        url: `register`,
        method: "POST",
        body,
      }),
    }),
    lookupUser: builder.mutation({
      query: (body) => ({
        url: `lookup-user`,
        method: "POST",
        body,
      }),
    }),
    verifyEmail: builder.mutation({
      query: (body) => ({
        url: `verify-email`,
        method: "POST",
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.data?.users?.is_verified) {
            dispatch(setUser(data.data?.users));
          }
        } catch (error) { }
      },
    }),
    rensendVerifyEmail: builder.mutation({
      query: (body) => ({
        url: `resend-verify-email`,
        method: "POST",
        body,
      }),
    }),
    getDashboardDetails: builder.query({
      query: () => {
        const qsBrand = new URLSearchParams(window.location.search).get('brand');
        const host = window.location.hostname.toLowerCase();
        const brand = (qsBrand === 'byzwash' || qsBrand === 'byzfunder')
          ? qsBrand
          : (host.includes('byzwash') ? 'byzwash' : 'byzfunder');

        return {
          url: `dashboard?brand=${brand}`,
          method: "GET",
        };
      },
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if(data?.data?.has_online_checkout){
            if (data.data?.data && data.data?.data.length > 0) {
              dispatch(setOnlineCheckout(data?.data?.data[0]));
            }
          }
        } catch (error) { }
      },
    }),
    requestPayoutLater: builder.mutation({
      query: (body) => {
        const qsBrand = new URLSearchParams(window.location.search).get('brand');
        const host = window.location.hostname.toLowerCase();
        const brand = (qsBrand === 'byzwash' || qsBrand === 'byzfunder')
          ? qsBrand
          : (host.includes('byzwash') ? 'byzwash' : 'byzfunder');
        return {
          url: `request-payoff-letter?brand=${brand}`,
          method: "POST",
          body,
        };
      },
    }),
    viewStatement: builder.mutation({
      query: (body) => ({
        url: `view-statement`,
        method: "POST",
        body,
      }),
    }),
    requestFunding: builder.mutation({
      query: (body) => ({
        url: `request-funding`,
        method: "POST",
        body,
      }),
    }),
    getImpersonateUser: builder.query({
      query: (params) => ({
        url: `impersonate-signin`,
        method: "GET",
        params,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.data?.users?.is_verified) {
            dispatch(setUser(data.data?.users));
            dispatch(setImpersonateUser());

          }
        } catch (error) { }
      },
    }),
    drawRequest: builder.mutation({
      query: (body) => ({
        url: `draw-request`,
        method: "POST",
        body,
      }),
    }),
    getDrawDetails: builder.query({
      query: (params) => ({
        url: `draw-details`,
        method: "GET",
        params
      }),
    }),
    getRequestFund: builder.query({
      query: (params) => ({
        url: `can-request-draw`,
        method: "GET",
        params
      }),
    }),
    getPaymentBreakoutDetails: builder.query({
      query: (params) => ({
        url: `payment-breakouts`,
        method: "GET",
        params
      }),
    }),
    referAFriend: builder.mutation({
      query: (body) => ({
        url: `refer-friend`,
        method: "POST",
        body,
      }),
    }),
    requestAllPayoutLater: builder.mutation({
      query: (body) => {
        const qsBrand = new URLSearchParams(window.location.search).get('brand');
        const host = window.location.hostname.toLowerCase();
        const brand = (qsBrand === 'byzwash' || qsBrand === 'byzfunder')
          ? qsBrand
          : (host.includes('byzwash') ? 'byzwash' : 'byzfunder');
        return {
          url: `request-payoff-letter-all?brand=${brand}`,
          method: "POST",
          body,
        };
      },
    }),
    sendBankConnectRequest: builder.mutation({
      query: (body) => ({
        url: `send-bank-connect-request`,
        method: "POST",
        body,
      }),
    }),
    getOfferDetails: builder.query({
      query: (body) => ({
        url: `offer-details`,
        method: "POST",
        body
      }),
    }),
    getPrepaySchedule: builder.query({
      query: (body) => ({
        url: `prepay-schedule`,
        method: "POST",
        body
      }),
    }),
    fetchBankConnectStatus: builder.mutation({
      query: (body) => ({
        url: `fetch-bank-connect-status`,
        method: "POST",
        body,
      }),
    }),
    sendViewOfferEmail: builder.mutation({
      query: (body) => ({
        url: `send-view-offer-email`,
        method: "POST",
        body,
      }),
    }),
    acceptOffer: builder.mutation({
      query: (body) => ({
        url: `accept-offer`,
        method: "POST",
        body,
      }),
    }),
    fetchRenewalUWStatus: builder.mutation({
      query: (body) => ({
        url: `get-renewal-status`,
        method: "POST",
        body,
      }),
    }),
    uploadStips: builder.mutation({
      query: (body) => ({
        url: `upload-stips`,
        method: "POST",
        body,
      }),
    }),
    declineBlocDraw: builder.mutation({
      query: (body) => ({
        url: `decline-bloc-draw`,
        method: "POST",
        body,
      }),
    }),


    // Online Checkout MCA APIs
    sendSocureVerfication: builder.mutation({
      query: (body) => ({
        url: `get-verification-url`,
        method: "POST",
        body,
      }),
    }),
    esignContract: builder.mutation({
      query: (body) => ({
        url: `generate-contract`,
        method: "POST",
        body,
      }),
    }),
    getPaymentSchedule: builder.query({
      query: (body) => ({
        url: `get-payment-schedule`,
        method: "POST",
        body,
      }),
    }),
    fetchSocureStatus: builder.mutation({
      query: (body) => ({
        url: `get-verification-status`,
        method: "POST",
        body,
      }),
    }),
    sendFailureNotif: builder.mutation({
      query: (body) => ({
        url: 'notify-mfa-failure',
        method: 'POST',
        body,
      }),
    }),
    getBankConnectStatus: builder.query({
      query: (body) => ({
        url: `get-bankconnect-status`,
        method: "POST",
        body,
      }),
    }),
    getOnlineCheckoutOffer: builder.query({
      query: (body) => ({
        url: `get-online-checkout-offer`,
        method: "POST",
        body,
      }),
    }),
    getReviewCheckoutOffer: builder.query({
      query: (body) => ({
        url: `get-lead-summary`,
        method: "POST",
        body,
      }),
    }),
    completeOnlineCheckoutOffer: builder.mutation({
      query: (body) => ({
        url: `complete-online-checkout`,
        method: "POST",
        body,
      }),
    }),
    // ENG-4373: Send secondary principal invite emails without completing checkout
    notifySecondaryPrincipals: builder.mutation({
      query: (body) => ({
        url: `notify-secondary-principals`,
        method: "POST",
        body,
      }),
    }),
    // ENG-4373: Poll for Contracts In status from ReviewCheckout waiting state
    checkContractStatus: builder.mutation({
      query: (body) => ({
        url: `check-contract-status`,
        method: "POST",
        body,
      }),
    }),
    notifyDistributionList: builder.mutation({
      query: (body) => ({
        url: 'notify-distribution-list',
        method: 'POST',
        body,
      }),
    }),
    saveAchSchedule: builder.mutation({
      query: (body) => ({
        url: 'save-ach-schedule',
        method: 'POST',
        body,
      }),
    }),
    notifyWrongBankInfoEmail: builder.mutation({
      query: (body) => ({
        url: 'notify-wrong-bank-info',
        method: 'POST',
        body,
      }),
    }),
    fetchBankDataFromRoutingNumber: builder.mutation({
      query: (body) => ({
        url: 'fetch-bank-data-from-routingnumber',
        method: 'POST',
        body,
      }),
    }),
    updateBankRoutingNumber: builder.mutation({
      query: (body) => ({
        url: 'update-bank-routing-number',
        method: 'POST',
        body,
      }),
    }),
    getCheckoutSessionState: builder.query({
      query: (params) => ({
        url: 'get-checkout-session-state',
        method: 'POST',
        params,
      }),
    }),
    viewOfferCompleteTime: builder.mutation({
      query: (body) => ({
        url: 'view-offer-complete-time',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useLoginUserMutation,
  useGoogleAuthMutation,
  useLogoutUserMutation,
  useForgotPasswordMutation,
  useChangePasswordMutation,
  useRegisterUserMutation,
  useLookupUserMutation,
  useVerifyEmailMutation,
  useRensendVerifyEmailMutation,
  useLazyGetDashboardDetailsQuery,
  useViewStatementMutation,
  useRequestPayoutLaterMutation,
  useRequestFundingMutation,
  useLazyGetImpersonateUserQuery,
  useDrawRequestMutation,
  useLazyGetDrawDetailsQuery,
  useLazyGetRequestFundQuery,
  useLazyGetPaymentBreakoutDetailsQuery,
  useReferAFriendMutation,
  useRequestAllPayoutLaterMutation,
  useSendBankConnectRequestMutation,
  useLazyGetOfferDetailsQuery,
  useLazyGetPrepayScheduleQuery,
  useFetchBankConnectStatusMutation,
  useSendViewOfferEmailMutation,
  useAcceptOfferMutation,
  useFetchRenewalUWStatusMutation,
  useUploadStipsMutation,
  useDeclineBlocDrawMutation,
  useEsignContractMutation,
  useLazyGetPaymentScheduleQuery,
  useSendFailureNotifMutation,
  useSendSocureVerficationMutation,
  useFetchSocureStatusMutation,
  useLazyGetBankConnectStatusQuery,
  useLazyGetOnlineCheckoutOfferQuery,
  useLazyGetReviewCheckoutOfferQuery,
  useNotifyDistributionListMutation,
  useCompleteOnlineCheckoutOfferMutation,
  useNotifySecondaryPrincipalsMutation,
  useCheckContractStatusMutation,
  useSaveAchScheduleMutation,
  useNotifyWrongBankInfoEmailMutation,
  useFetchBankDataFromRoutingNumberMutation,
  useUpdateBankRoutingNumberMutation,
  useLazyGetCheckoutSessionStateQuery,
  useViewOfferCompleteTimeMutation,
} = authApi;